"use client"

import { useState, useEffect, useCallback } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { getDoctorsAction, deleteDoctorAction } from "../../../store/actions/DoctorsAction"
import swal from "sweetalert"
import Loader from "../Common/Loader"

const Doctor = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const doctors = useSelector((state) => state.doctors.doctors || [])
  const numRows = useSelector((state) => state.doctors.num_rows || 0)

  const [searchQueries, setSearchQueries] = useState({
    doctor_id: "",
    doctor_name: "",
    specialization: "",
    doctor_status: "",
  })

  const [activeSearchField, setActiveSearchField] = useState("")
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("")
  const [sortField, setSortField] = useState("doctor_id")
  const [sortOrder, setSortOrder] = useState("asc")
  const [currentPage, setCurrentPage] = useState(0)
  const rowsPerPage = 10 // This should ideally come from a config or be dynamic if needed
  const [initialLoading, setInitialLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState("")

  const fetchDoctorsFromServer = useCallback(
    async (
      pagination = true,
      pageid = 0,
      search_by = activeSearchField,
      search_keyword = activeSearchKeyword,
      sort_by = sortField,
      sort_order = sortOrder,
    ) => {
      setError("")
      try {
        await dispatch(getDoctorsAction(pagination, pageid, search_by, search_keyword, sort_by, sort_order))
      } catch (err) {
        console.error("Failed to fetch doctors:", err)
        setError("Failed to load doctors. Please try again.")
      }
    },
    [dispatch, activeSearchField, activeSearchKeyword, sortField, sortOrder],
  )

  useEffect(() => {
    const fetchInitialData = async () => {
      setInitialLoading(true)
      setError("")
      try {
        await dispatch(getDoctorsAction(true, 0, "", "", "doctor_id", "asc"))
      } catch (err) {
        console.error("Failed to fetch doctors:", err)
        setError("Failed to load doctors. Please try again.")
      } finally {
        setInitialLoading(false)
      }
    }
    fetchInitialData()
  }, [dispatch]) // Only depends on dispatch, runs once

  useEffect(() => {
    // Skip if this is the initial load
    if (initialLoading) return

    const fetchData = async () => {
      setError("")
      try {
        await dispatch(
          getDoctorsAction(true, currentPage, activeSearchField, activeSearchKeyword, sortField, sortOrder),
        )
      } catch (err) {
        console.error("Failed to fetch doctors:", err)
        setError("Failed to load doctors. Please try again.")
      }
    }
    fetchData()
  }, [dispatch, currentPage, activeSearchField, activeSearchKeyword, sortField, sortOrder, initialLoading])

  const handleSearch = (e, field) => {
    const keyword = e.target.value

    // Clear all search queries first, then set the current field
    const clearedQueries = {
      doctor_id: "",
      doctor_name: "",
      specialization: "",
      doctor_status: "",
    }

    const updatedQueries = { ...clearedQueries, [field]: keyword }
    setSearchQueries(updatedQueries)
    setCurrentPage(0) // Reset to first page on new search

    setActiveSearchField(field)
    setActiveSearchKeyword(keyword)
    // The useEffect will trigger fetchDoctorsFromServer with updated state
  }

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc"
    setSortField(field)
    setSortOrder(order)
    setCurrentPage(0) // Reset to first page on new sort
    // The useEffect will trigger fetchDoctorsFromServer with updated state
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    // The useEffect will trigger fetchDoctorsFromServer with updated currentPage
  }

  const handleEdit = (doctor) => {
    navigate("/update-doctor", { state: { doctor } })
  }

  const handleDelete = (doctor) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this doctor!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setActionLoading(true)
          await dispatch(deleteDoctorAction(doctor.doctor_id)) // Assuming doctor.doctor_id is the correct ID
          // Re-fetch data after deletion to update the list
          fetchDoctorsFromServer(true, currentPage)
        } catch (err) {
          setError("Failed to delete doctor")
          console.error("Delete failed:", err)
        } finally {
          setActionLoading(false)
        }
      }
    })
  }

  const totalPages = Math.ceil(numRows / rowsPerPage)

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/add-doctor" className="btn btn-primary btn-rounded">
            + Add Doctor
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {error && <div className="text-danger mb-2">{error}</div>}
              {initialLoading ? (
                <div className="text-center my-5">
                  <Loader size="lg" />
                </div>
              ) : (
                <>
                  {actionLoading && (
                    <div className="text-center mb-3">
                      <Loader size="sm" />
                      <small className="text-muted ms-2">Processing...</small>
                    </div>
                  )}

                  <div className="table-responsive">
                    <table className="table table-striped mb-4">
                      <thead className="table-light">
                        <tr>
                          <th onClick={() => handleSort("doctor_id")} style={{ cursor: "pointer" }}>
                            Doctor ID {sortField === "doctor_id" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.doctor_id}
                              onChange={(e) => handleSearch(e, "doctor_id")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search ID"
                            />
                          </th>
                          <th onClick={() => handleSort("doctor_name")} style={{ cursor: "pointer" }}>
                            Name {sortField === "doctor_name" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.doctor_name}
                              onChange={(e) => handleSearch(e, "doctor_name")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Name"
                            />
                          </th>
                          <th onClick={() => handleSort("specialization")} style={{ cursor: "pointer" }}>
                            Specialization {sortField === "specialization" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.specialization}
                              onChange={(e) => handleSearch(e, "specialization")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Specialization"
                            />
                          </th>
                          <th onClick={() => handleSort("doctor_status")} style={{ cursor: "pointer" }}>
                            Status {sortField === "doctor_status" && (sortOrder === "asc" ? "↑" : "↓")}
                            <input
                              className="form-control form-control-sm mt-1"
                              value={searchQueries.doctor_status}
                              onChange={(e) => handleSearch(e, "doctor_status")}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search Status"
                            />
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {doctors.map((doctor) => (
                          <tr key={doctor.doctor_id}>
                            <td>{doctor.doctor_id}</td>
                            <td>{doctor.doctor_name}</td>
                            <td>{doctor.specialization}</td>
                            <td>
                              <span
                                className={`badge px-3 py-1 rounded-pill text-white ${
                                  doctor.doctor_status.toLowerCase() === "active" ? "bg-success" : "bg-danger"
                                }`}
                              >
                                {doctor.doctor_status}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex justify-content-around">
                                <span
                                  className="edit me-2"
                                  onClick={() => handleEdit(doctor)}
                                  style={{ cursor: "pointer" }}
                                  title="Edit Doctor"
                                >
                                  <i className="fa fa-pencil-alt text-success"></i>
                                </span>
                                <span
                                  className="delete me-2"
                                  onClick={() => handleDelete(doctor)}
                                  style={{ cursor: "pointer" }}
                                  title="Delete Doctor"
                                >
                                  <i className="fa fa-trash text-danger"></i>
                                </span>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {doctors.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No doctors found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="pagination-wrapper"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "20px",
                      flexWrap: "wrap",
                      flexDirection: "column", // Show message above the buttons
                    }}
                  >
                    <div>
                      Showing {numRows === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                      {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "center" }}>
                      <button
                        // onClick={() => handlePageChange(currentPage - 1)}
                        onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                        disabled={currentPage === 0}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === 0 ? "not-allowed" : "pointer",
                          opacity: currentPage === 0 ? 0.5 : 1,
                        }}
                      >
                        &lt;
                      </button>
                      {(() => {
                        const pages = []
                        const maxPagesToShow = 5
                        const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow))
                        const endPage = Math.min(startPage + maxPagesToShow, totalPages)

                        if (startPage > 0) {
                          pages.push(
                            <button
                              key={0}
                              onClick={() => handlePageChange(0)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              1
                            </button>,
                          )
                          if (startPage > 1) {
                            pages.push(
                              <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                                ...
                              </span>,
                            )
                          }
                        }

                        for (let i = startPage; i < endPage; i++) {
                          const isActive = currentPage === i
                          pages.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: isActive ? "#007bff" : "#fff",
                                color: isActive ? "#fff" : "#000",
                                cursor: "pointer",
                              }}
                            >
                              {i + 1}
                            </button>,
                          )
                        }

                        if (endPage < totalPages) {
                          if (endPage < totalPages - 1) {
                            pages.push(
                              <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                                ...
                              </span>,
                            )
                          }
                          pages.push(
                            <button
                              key={totalPages - 1}
                              onClick={() => handlePageChange(totalPages - 1)}
                              style={{
                                padding: "6px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                color: "#000",
                                cursor: "pointer",
                              }}
                            >
                              {totalPages}
                            </button>,
                          )
                        }

                        return pages
                      })()}
                      <button
                        // onClick={() => handlePageChange(currentPage + 1)}
                        onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                        disabled={currentPage === totalPages - 1}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: "#fff",
                          color: "#000",
                          cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                          opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                        }}
                      >
                        &gt;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Doctor;
